import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './Leaderboard.css';

const Leaderboard = ({ userId }) => {
  const [leaderboardType, setLeaderboardType] = useState('xp'); // xp, battles, streak
  const [timeRange, setTimeRange] = useState('allTime'); // today, week, month, allTime
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadLeaderboard();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaderboardType, timeRange]);

  const loadLeaderboard = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(
        `/api/gamification/leaderboard?type=${leaderboardType}&range=${timeRange}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setLeaderboardData(response.data.leaderboard || []);
      setUserRank(response.data.userRank);
    } catch (err) {
      console.error('Error loading leaderboard:', err);
      setError('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'xp': return '⭐';
      case 'battles': return '⚔️';
      case 'streak': return '🔥';
      default: return '🏆';
    }
  };

  // eslint-disable-next-line no-unused-vars
  const _getTypeLabel = (type) => { // Reserved for future use
    switch (type) {
      case 'xp': return 'XP Points';
      case 'battles': return 'Battle Wins';
      case 'streak': return 'Daily Streak';
      default: return 'Rank';
    }
  };

  const getRankMedal = (rank) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `#${rank}`;
    }
  };

  const getRankColor = (rank) => {
    switch (rank) {
      case 1: return 'rank-gold';
      case 2: return 'rank-silver';
      case 3: return 'rank-bronze';
      default: return '';
    }
  };

  const formatScore = (score, type) => {
    switch (type) {
      case 'xp': return `${score.toLocaleString()} XP`;
      case 'battles': return `${score} Wins`;
      case 'streak': return `${score} Days`;
      default: return score;
    }
  };

  return (
    <div className="leaderboard-container">
      {/* Header */}
      <div className="leaderboard-header">
        <h2>🏆 Leaderboard</h2>
        <p className="subtitle">Compete with others and climb the ranks!</p>
      </div>

      {/* Filters */}
      <div className="filters-section">
        {/* Type Filter */}
        <div className="filter-group">
          <label>Leaderboard Type:</label>
          <div className="filter-buttons">
            <button
              className={`filter-btn ${leaderboardType === 'xp' ? 'active' : ''}`}
              onClick={() => setLeaderboardType('xp')}
            >
              ⭐ XP Points
            </button>
            <button
              className={`filter-btn ${leaderboardType === 'battles' ? 'active' : ''}`}
              onClick={() => setLeaderboardType('battles')}
            >
              ⚔️ Battle Wins
            </button>
            <button
              className={`filter-btn ${leaderboardType === 'streak' ? 'active' : ''}`}
              onClick={() => setLeaderboardType('streak')}
            >
              🔥 Daily Streak
            </button>
          </div>
        </div>

        {/* Time Range Filter */}
        <div className="filter-group">
          <label>Time Range:</label>
          <div className="filter-buttons">
            <button
              className={`filter-btn ${timeRange === 'today' ? 'active' : ''}`}
              onClick={() => setTimeRange('today')}
            >
              Today
            </button>
            <button
              className={`filter-btn ${timeRange === 'week' ? 'active' : ''}`}
              onClick={() => setTimeRange('week')}
            >
              This Week
            </button>
            <button
              className={`filter-btn ${timeRange === 'month' ? 'active' : ''}`}
              onClick={() => setTimeRange('month')}
            >
              This Month
            </button>
            <button
              className={`filter-btn ${timeRange === 'allTime' ? 'active' : ''}`}
              onClick={() => setTimeRange('allTime')}
            >
              All Time
            </button>
          </div>
        </div>
      </div>

      {/* User Rank Card */}
      {userRank && (
        <div className="user-rank-card">
          <div className="user-rank-header">
            <span>Your Ranking</span>
          </div>
          <div className="user-rank-content">
            <div className="user-rank-position">
              <span className="rank-badge">{getRankMedal(userRank.rank)}</span>
              <span className="rank-label">Rank {userRank.rank}</span>
            </div>
            <div className="user-rank-score">
              <span className="score-icon">{getTypeIcon(leaderboardType)}</span>
              <span className="score-value">{formatScore(userRank.score, leaderboardType)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard Table */}
      <div className="leaderboard-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading leaderboard...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <span className="error-icon">⚠️</span>
            <p>{error}</p>
          </div>
        ) : leaderboardData.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🏆</div>
            <h3>No Data Yet</h3>
            <p>Be the first to appear on the leaderboard!</p>
          </div>
        ) : (
          <div className="leaderboard-table">
            {/* Top 3 Podium */}
            <div className="podium">
              {leaderboardData.slice(0, 3).map((user, index) => {
                const position = index + 1;
                return (
                  <div
                    key={user.userId}
                    className={`podium-place place-${position} ${
                      user.userId === userId ? 'current-user' : ''
                    }`}
                  >
                    <div className="podium-medal">{getRankMedal(position)}</div>
                    <div className="podium-avatar">
                      <span>{user.username?.charAt(0).toUpperCase() || '?'}</span>
                    </div>
                    <div className="podium-name">{user.username || 'Anonymous'}</div>
                    <div className="podium-score">
                      {formatScore(user.score, leaderboardType)}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Rest of Leaderboard */}
            <div className="leaderboard-list">
              {leaderboardData.slice(3).map((user, index) => {
                const rank = index + 4;
                return (
                  <div
                    key={user.userId}
                    className={`leaderboard-row ${
                      user.userId === userId ? 'current-user' : ''
                    }`}
                  >
                    <div className="rank-cell">
                      <span className={`rank-number ${getRankColor(rank)}`}>
                        {getRankMedal(rank)}
                      </span>
                    </div>
                    <div className="user-cell">
                      <div className="user-avatar">
                        <span>{user.username?.charAt(0).toUpperCase() || '?'}</span>
                      </div>
                      <div className="user-info">
                        <div className="user-name">
                          {user.username || 'Anonymous'}
                          {user.userId === userId && (
                            <span className="you-badge">You</span>
                          )}
                        </div>
                        {user.level && (
                          <div className="user-level">Level {user.level}</div>
                        )}
                      </div>
                    </div>
                    <div className="score-cell">
                      <span className="score-icon">{getTypeIcon(leaderboardType)}</span>
                      <span className="score-text">
                        {formatScore(user.score, leaderboardType)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Stats Summary */}
      {leaderboardData.length > 0 && (
        <div className="stats-summary">
          <div className="stat-box">
            <div className="stat-label">Total Players</div>
            <div className="stat-value">{leaderboardData.length}</div>
          </div>
          <div className="stat-box">
            <div className="stat-label">Top Score</div>
            <div className="stat-value">
              {formatScore(leaderboardData[0]?.score || 0, leaderboardType)}
            </div>
          </div>
          <div className="stat-box">
            <div className="stat-label">Your Position</div>
            <div className="stat-value">
              {userRank ? `#${userRank.rank}` : 'N/A'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaderboard;


